import * as React from 'react';
import { CollapsibleCard, FormField, Input, Textarea, cn } from '@insolech/ui';
import type {
  SandalDesignState,
  SandalModel,
  SandalBaseColor,
  SandalUpperStrapColor,
  SandalTopCover,
  SandalYesNo,
  SandalUpperStrapSize,
} from './prescriptionState';
import {
  SANDAL_MODEL_LABELS,
  modelOffersUpperStrap,
  upperStrapChoicesFor,
} from './sandalStrapOptions';

/**
 * SandalStyleSection (R36 rebuild).
 *
 * Replaces the legacy variant grid (Buckle / Velcro + four colour combos)
 * with a full sandal configurator matching how the lab actually builds
 * them:
 *
 *   Row 1  Sandal model picker         (Buckle / Velcro / Flipflop)
 *   Row 2  Base color · Upper strap color · Top cover     (grid-cols-3)
 *   Row 3  White midsole · Posterior strap · Upper strap size
 *   Row 4  Custom name                 (full-width text input)
 *
 * Every field is required: the wizard's Next-button gate
 * (collectSandalDesignMissing) blocks until model + all six selects +
 * custom name are filled.
 *
 * Image cards mirror SolutionSubtypePicker — large clickable squares with
 * a product photo, label underneath, brand-500 ring when selected. Each
 * image has an onError fallback to /products/ots-placeholder.svg so a
 * missing upload doesn't break the form.
 *
 * NOTE: the photos live at /products/sandals/buckle.JPG,
 * /products/sandals/velcro.JPG and /products/sandals/flipflop.JPG inside
 * apps/web/public/. The same paths exist on the marketing site if/when
 * we surface the configurator there.
 */

interface Props {
  value: SandalDesignState;
  onChange: (next: SandalDesignState) => void;
  readOnly?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  /** Show inline validation errors (set true after Continue is attempted). */
  showErrors?: boolean | undefined;
  /** Patient name for the one-shot "Copy patient name" action on the custom
   *  name field (parity with InsoleDesignSection). */
  patientName?: string | null | undefined;
}

interface ModelOption {
  id: SandalModel;
  label: string;
  src: string;
}

export const MODEL_OPTIONS: ReadonlyArray<ModelOption> = [
  { id: 'buckle', label: SANDAL_MODEL_LABELS.buckle, src: '/products/sandals/buckle.JPG' },
  { id: 'velcro', label: SANDAL_MODEL_LABELS.velcro, src: '/products/sandals/velcro.JPG' },
  { id: 'flipflop', label: SANDAL_MODEL_LABELS.flipflop, src: '/products/sandals/flipflop.JPG' },
];

const FALLBACK_IMG = '/products/ots-placeholder.svg';

export const BASE_COLOR_OPTIONS: ReadonlyArray<{ value: SandalBaseColor; label: string }> = [
  { value: 'black', label: 'Black' },
  { value: 'grey', label: 'Grey' },
  { value: 'beige', label: 'Beige' },
  { value: 'blue', label: 'Blue' },
];

// Upper strap colours live in sandalStrapOptions.ts: the base six less a
// model's exclusions (Velcro takes no Sand beige or Mustard yellow since
// 2026-11-03), plus Dark blue for the models listed there (Buckle and Velcro).

export const TOP_COVER_OPTIONS: ReadonlyArray<{ value: SandalTopCover; label: string }> = [
  { value: 'beige', label: 'Beige' },
  { value: 'anthracite', label: 'Anthracite' },
];

export const YES_NO_OPTIONS: ReadonlyArray<{ value: SandalYesNo; label: string }> = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

export const UPPER_STRAP_SIZE_OPTIONS: ReadonlyArray<{
  value: SandalUpperStrapSize;
  label: string;
}> = [
  { value: 'standard', label: 'Standard size' },
  { value: 'large_cavus', label: 'Large size (recommended for cavus foot)' },
];

/** Compact native <select> styled like the rest of the prescription form. */
function Dropdown<T extends string>({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  invalid,
}: {
  value: T | undefined;
  onChange: (next: T | undefined) => void;
  /** `disabled` marks an entry that is shown but cannot be (re)picked: the
   *  line's saved value that its model no longer offers. */
  options: ReadonlyArray<{ value: T; label: string; disabled?: boolean | undefined }>;
  placeholder: string;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange((e.target.value || undefined) as T | undefined)}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      className={cn(
        'w-full h-8 rounded-md border bg-surface-elevated px-2 text-sm',
        'focus:outline-none focus:ring-2 focus:ring-brand-500',
        invalid ? 'border-error-500' : 'border-border-default',
      )}
    >
      <option value="" disabled hidden>
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function SandalStyleSection({
  value,
  onChange,
  readOnly,
  defaultOpen = true,
  showErrors = false,
  patientName,
}: Props) {
  const set = <K extends keyof SandalDesignState>(k: K, v: SandalDesignState[K]) =>
    onChange({ ...value, [k]: v });

  // One-shot "Copy patient name" for the custom name field (parity with
  // InsoleDesignSection). Checked state is cosmetic; the operator can freely
  // overwrite the field afterwards.
  const [copyPatientName, setCopyPatientName] = React.useState(false);

  const selectModel = (next: SandalModel) => {
    if (readOnly) return;
    // If the target model does not offer the strap colour already picked
    // (Flipflop for Dark blue; Velcro for Sand beige and Mustard yellow),
    // clear it so we don't leave an option the dropdown no longer offers and
    // the lab cannot build.
    const clearStrap = !modelOffersUpperStrap(next, value.upper_strap_color);
    onChange({
      ...value,
      model: next,
      ...(clearStrap ? { upper_strap_color: undefined } : {}),
    });
  };

  // The model's colours, plus the line's saved colour as a disabled, named
  // entry when the model no longer offers it: an older line (Velcro with
  // Sand beige before 2026-11-03) is shown as ordered, never recoloured.
  const upperStrapOptions: ReadonlyArray<{
    value: SandalUpperStrapColor;
    label: string;
    disabled?: boolean | undefined;
  }> = upperStrapChoicesFor(value.model, value.upper_strap_color).map((o) => ({
    value: o.value,
    label: o.label,
    ...(o.legacy ? { disabled: true } : {}),
  }));

  // Required-field flags, painted red on showErrors.
  const missing = {
    model: !value.model,
    base_color: !value.base_color,
    upper_strap_color: !value.upper_strap_color,
    top_cover: !value.top_cover,
    white_midsole: !value.white_midsole,
    posterior_strap: !value.posterior_strap,
    upper_strap_size: !value.upper_strap_size,
    // Custom name is OPTIONAL — same convention as InsoleDesignSection
    // ("Patient name to print on insole (optional)").
  };

  const showMissing = (k: keyof typeof missing) => showErrors && missing[k];

  return (
    <CollapsibleCard
      title="Sandal style"
      subtitle="Model, colors, features and the custom name engraved on the sandal"
      defaultOpen={defaultOpen}
    >
      <div className="space-y-4">
        {/* Row 1 — Sandal model picker. Three image cards, styled like the
            Solution-subtype picker on step 2. */}
        <FormField
          label="Sandal model"
          required
          error={showMissing('model') ? 'Pick a sandal model' : undefined}
        >
          <ul
            role="radiogroup"
            aria-label="Sandal model"
            className="grid grid-cols-3 gap-2"
          >
            {MODEL_OPTIONS.map((opt) => {
              const selected = value.model === opt.id;
              return (
                <li key={opt.id}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`sandal-model-${opt.id}`}
                    onClick={() => selectModel(opt.id)}
                    disabled={readOnly}
                    className={cn(
                      'group w-full text-start p-2 rounded-lg border-2 transition-all bg-surface-elevated',
                      selected
                        ? 'border-brand-500 ring-2 ring-brand-500/20 bg-brand-50'
                        : 'border-border-subtle hover:border-brand-300',
                      readOnly ? 'cursor-default opacity-90' : 'cursor-pointer',
                    )}
                  >
                    <div className="aspect-[4/3] w-full rounded-md bg-white overflow-hidden flex items-center justify-center">
                      <img
                        src={opt.src}
                        alt={opt.label}
                        loading="lazy"
                        onError={(e) => {
                          const img = e.currentTarget;
                          if (img.src.endsWith(FALLBACK_IMG)) return;
                          img.src = FALLBACK_IMG;
                        }}
                        className="h-full w-full object-contain p-2"
                      />
                    </div>
                    <div className="mt-1.5 text-center">
                      <h3 className="text-sm font-semibold text-text-primary leading-tight">
                        {opt.label}
                      </h3>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </FormField>

        {/* Row 2 — Color selections. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <FormField
            label="Base color"
            required
            error={showMissing('base_color') ? 'Required' : undefined}
          >
            <Dropdown
              value={value.base_color}
              onChange={(v) => set('base_color', v)}
              options={BASE_COLOR_OPTIONS}
              placeholder="Select base color"
              disabled={readOnly}
              invalid={showMissing('base_color')}
            />
          </FormField>
          <FormField
            label="Upper strap color"
            required
            error={showMissing('upper_strap_color') ? 'Required' : undefined}
          >
            <Dropdown
              value={value.upper_strap_color}
              onChange={(v) => set('upper_strap_color', v)}
              options={upperStrapOptions}
              placeholder="Select upper strap color"
              disabled={readOnly}
              invalid={showMissing('upper_strap_color')}
            />
          </FormField>
          <FormField
            label="Top cover"
            required
            error={showMissing('top_cover') ? 'Required' : undefined}
          >
            <Dropdown
              value={value.top_cover}
              onChange={(v) => set('top_cover', v)}
              options={TOP_COVER_OPTIONS}
              placeholder="Select top cover"
              disabled={readOnly}
              invalid={showMissing('top_cover')}
            />
          </FormField>
        </div>

        {/* Row 3 — Sandal features. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <FormField
            label="White midsole"
            required
            error={showMissing('white_midsole') ? 'Required' : undefined}
          >
            <Dropdown
              value={value.white_midsole}
              onChange={(v) => set('white_midsole', v)}
              options={YES_NO_OPTIONS}
              placeholder="Select"
              disabled={readOnly}
              invalid={showMissing('white_midsole')}
            />
          </FormField>
          <FormField
            label="Posterior strap"
            required
            error={showMissing('posterior_strap') ? 'Required' : undefined}
          >
            <Dropdown
              value={value.posterior_strap}
              onChange={(v) => set('posterior_strap', v)}
              options={YES_NO_OPTIONS}
              placeholder="Select"
              disabled={readOnly}
              invalid={showMissing('posterior_strap')}
            />
          </FormField>
          <FormField
            label="Upper strap size"
            required
            error={showMissing('upper_strap_size') ? 'Required' : undefined}
          >
            <Dropdown
              value={value.upper_strap_size}
              onChange={(v) => set('upper_strap_size', v)}
              options={UPPER_STRAP_SIZE_OPTIONS}
              placeholder="Select strap size"
              disabled={readOnly}
              invalid={showMissing('upper_strap_size')}
            />
          </FormField>
        </div>

        {/* Row 4 — Custom name + Design remarks. Both optional, matching
            the InsoleDesignSection convention. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Custom name">
            <Input
              value={value.custom_name ?? ''}
              onChange={(e) => set('custom_name', e.target.value)}
              disabled={readOnly}
              className="h-8"
              placeholder="Patient name or logo (optional)"
              data-testid="sandal-custom-name-input"
            />
            {/* Copy patient name - one-shot copy from the patient section.
                Writes nothing when the patient name is empty. */}
            {!readOnly ? (
              <label className="mt-1.5 inline-flex items-center gap-2 text-xs text-text-secondary cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="sandal-custom-name-copy-patient"
                  checked={copyPatientName}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setCopyPatientName(checked);
                    const name = (patientName ?? '').trim();
                    if (checked && name) set('custom_name', name);
                  }}
                  className="h-3.5 w-3.5 rounded border-border-default text-brand-600 focus:ring-brand-500"
                />
                Copy patient name
              </label>
            ) : null}
          </FormField>
          <FormField label="Design remarks">
            <Textarea
              value={value.design_remarks ?? ''}
              onChange={(e) => set('design_remarks', e.target.value)}
              disabled={readOnly}
              className="min-h-[72px] text-sm"
              placeholder="Any extra design details for the lab…"
            />
          </FormField>
        </div>
      </div>
    </CollapsibleCard>
  );
}
